import React, { useEffect, useRef, useState, useCallback } from "react";
import { TelnyxRTC } from "@telnyx/webrtc";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import {
    Phone, PhoneOff, PhoneIncoming, PhoneOutgoing, PhoneMissed, Loader2, Signal, Info,
    Delete, Mic, MicOff, Volume2, VolumeX, Grid3x3, User, Clock, X, Star, Users, Plus,
} from "lucide-react";
import { toast } from "sonner";

// In-app calling for iPhone / Android / browser via the Telnyx WebRTC SDK.
// WhatsApp-style softphone: keypad + recents tabs, full-screen incoming / outgoing / active
// call overlays, in-call controls (mute / speaker / keypad-DTMF), and a live call timer.
// Works on any FlexLine Mobile (Telnyx) number once live provisioning is on.

const KEYS = [
    { d: "1", s: "" }, { d: "2", s: "ABC" }, { d: "3", s: "DEF" },
    { d: "4", s: "GHI" }, { d: "5", s: "JKL" }, { d: "6", s: "MNO" },
    { d: "7", s: "PQRS" }, { d: "8", s: "TUV" }, { d: "9", s: "WXYZ" },
    { d: "*", s: "" }, { d: "0", s: "+" }, { d: "#", s: "" },
];

const RECENTS_KEY = "flexline_recent_calls";
const loadRecents = () => {
    try { return JSON.parse(localStorage.getItem(RECENTS_KEY) || "[]"); } catch { return []; }
};
const fmtTime = (t) => {
    const d = new Date(t);
    const today = new Date();
    const same = d.toDateString() === today.toDateString();
    return same ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
        : d.toLocaleDateString([], { month: "short", day: "numeric" });
};
const fmtDuration = (s) => {
    const m = Math.floor(s / 60), sec = s % 60;
    return `${m}:${String(sec).padStart(2, "0")}`;
};

// Telnyx needs a real E.164 number to route a PSTN call. The dialpad lets people type
// 10-digit US numbers, so normalize before dialing (+1 for US, keep any explicit +country).
const toE164 = (raw) => {
    let s = (raw || "").trim();
    if (s.startsWith("+")) return "+" + s.slice(1).replace(/\D/g, "");
    const d = s.replace(/\D/g, "");
    if (d.length === 10) return "+1" + d;
    if (d.length === 11 && d.startsWith("1")) return "+" + d;
    return d ? "+" + d : "";
};

// Attach the far-end audio to the <audio> element and force playback (iOS Safari needs this).
const playRemote = (call) => {
    try {
        const el = document.getElementById("flexline-remote-audio");
        const stream = call?.remoteStream || call?.options?.remoteStream;
        if (el && stream) { el.srcObject = stream; el.play?.().catch(() => {}); }
    } catch {}
};

export const Softphone = () => {
    const [state, setState] = useState("idle"); // idle | loading | ready | unavailable | error
    const [detail, setDetail] = useState("");
    const [callerNumber, setCallerNumber] = useState("");
    const [tab, setTab] = useState("keypad"); // keypad | recents
    const [dest, setDest] = useState("");
    const [activeCall, setActiveCall] = useState(null);
    const [incoming, setIncoming] = useState(null);
    const [callStatus, setCallStatus] = useState(""); // telnyx call.state
    const [outgoing, setOutgoing] = useState(false);
    const [muted, setMuted] = useState(false);
    const [speaker, setSpeaker] = useState(false);
    const [showInCallPad, setShowInCallPad] = useState(false);
    const [seconds, setSeconds] = useState(0);
    const [recents, setRecents] = useState(loadRecents);
    const [favorites, setFavorites] = useState([]);
    const clientRef = useRef(null);
    const timerRef = useRef(null);
    const pendingOutboundRef = useRef(null);

    const loadFavorites = useCallback(async () => {
        try { const { data } = await api.get("/favorites"); setFavorites(data.favorites || []); } catch { }
    }, []);

    const addRecent = useCallback((number, direction) => {
        setRecents((prev) => {
            const next = [{ number, direction, at: Date.now() }, ...prev].slice(0, 30);
            try { localStorage.setItem(RECENTS_KEY, JSON.stringify(next)); } catch { }
            return next;
        });
    }, []);

    // On iOS/Safari, WebRTC + microphone access require a real user gesture, so we
    // connect only after the user taps "Enable calling" (not automatically on mount).
    const initCall = async () => {
        setState("loading");
        setDetail("");
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach((t) => t.stop());
        } catch (e) {
            setState("error");
            setDetail("Microphone access is needed for calling. Please allow the microphone permission and try again.");
            return;
        }
        try {
            const { data } = await api.post("/telnyx/webrtc/token");
            setCallerNumber(data.caller_number || "");
            const client = new TelnyxRTC({ login_token: data.token });
            client.remoteElement = "flexline-remote-audio";
            client.on("telnyx.ready", () => { setState("ready"); loadFavorites(); });
            client.on("telnyx.error", () => { setState("error"); setDetail("Voice connection error. Please try again."); });
            client.on("telnyx.socket.close", () => { if (clientRef.current) setState((s) => (s === "ready" ? "error" : s)); });
            client.on("telnyx.notification", (n) => {
                if (n.type !== "callUpdate" || !n.call) return;
                const call = n.call;
                setCallStatus(call.state);
                if (call.state === "ringing" && call.direction === "inbound") {
                    if (pendingOutboundRef.current) {
                        // This is our own outbound-bridge leg ringing back — auto-answer it.
                        try { call.answer(); } catch { }
                        setActiveCall(call);
                        setOutgoing(true);
                    } else {
                        setIncoming(call);
                    }
                }
                if (["active", "held", "requesting", "trying", "early"].includes(call.state)) {
                    setActiveCall(call);
                    setIncoming(null);
                    if (call.state === "active") playRemote(call);
                }
                if (["hangup", "destroy"].includes(call.state)) {
                    const cause = call.cause || call.causeCode || call.options?.causeCode;
                    if (cause && !["NORMAL_CLEARING", "USER_BUSY", "ORIGINATOR_CANCEL"].includes(cause)) {
                        toast.error(`Call could not connect: ${cause}`);
                    }
                    pendingOutboundRef.current = null;
                    setActiveCall(null);
                    setIncoming(null);
                    setOutgoing(false);
                    setCallStatus("");
                    setMuted(false);
                    setShowInCallPad(false);
                }
            });
            client.connect();
            clientRef.current = client;
            setTimeout(() => {
                setState((s) => {
                    if (s === "loading") { setDetail("Couldn't connect the voice line. Check your internet and tap to retry."); return "error"; }
                    return s;
                });
            }, 20000);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 503 || status === 404) {
                setState("unavailable");
                setDetail(e?.response?.data?.detail || "In-app calling is not available yet.");
            } else {
                setState("error");
                setDetail(e?.response?.data?.detail || "Could not start the voice session.");
            }
        }
    };

    useEffect(() => {
        return () => { try { clientRef.current?.disconnect(); } catch { } };
    }, []);

    // Call timer — starts ticking once the call is active.
    useEffect(() => {
        if (callStatus === "active") {
            if (!timerRef.current) {
                timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
            }
        } else {
            clearInterval(timerRef.current);
            timerRef.current = null;
            if (!activeCall) setSeconds(0);
        }
        return () => { };
    }, [callStatus, activeCall]);

    const placeCall = async (numberArg) => {
        const raw = (typeof numberArg === "string" ? numberArg : dest).trim();
        const target = toE164(raw);
        if (!target || !clientRef.current) return;
        // Outbound via the server bridge so the call shows YOUR number as caller ID and
        // avoids Telnyx cross-connection rejection. The app auto-answers the ring-back leg.
        pendingOutboundRef.current = target;
        setDest(target);
        setOutgoing(true);
        setSeconds(0);
        addRecent(target, "outgoing");
        try {
            await api.post("/telnyx/outbound", { to: target });
        } catch (e) {
            pendingOutboundRef.current = null;
            setOutgoing(false);
            toast.error(e?.response?.data?.detail || "Could not start the call.");
        }
    };

    const addFavorite = async (number, name) => {
        try {
            const { data } = await api.post("/favorites", { number, name: name || "" });
            setFavorites(data.favorites || []);
            toast.success("Added to favorites");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not add favorite");
        }
    };

    const removeFavorite = async (number) => {
        try {
            const { data } = await api.post("/favorites/remove", { number });
            setFavorites(data.favorites || []);
        } catch { }
    };

    const hangup = () => { try { activeCall?.hangup(); } catch { } pendingOutboundRef.current = null; setActiveCall(null); setOutgoing(false); };
    const answer = () => {
        try { incoming?.answer(); } catch { }
        if (incoming?.options?.remoteCallerNumber) addRecent(incoming.options.remoteCallerNumber, "incoming");
        setActiveCall(incoming); setIncoming(null);
    };
    const reject = () => {
        try { incoming?.hangup(); } catch { }
        if (incoming?.options?.remoteCallerNumber) addRecent(incoming.options.remoteCallerNumber, "missed");
        setIncoming(null);
    };
    const press = (d) => setDest((v) => (v + d).slice(0, 20));
    const backspace = () => setDest((v) => v.slice(0, -1));
    const sendDtmf = (d) => { try { activeCall?.dtmf(d); } catch { } };
    const toggleMute = () => {
        try { muted ? activeCall?.unmuteAudio() : activeCall?.muteAudio(); } catch { }
        setMuted((m) => !m);
    };
    const toggleSpeaker = () => {
        const el = document.getElementById("flexline-remote-audio");
        if (el) el.volume = speaker ? 1 : 1; // volume already max; toggle is a UI affordance on web
        setSpeaker((s) => !s);
    };

    // Keyboard support on the keypad screen.
    useEffect(() => {
        if (state !== "ready" || activeCall || incoming) return;
        const onKey = (e) => {
            if (e.target.tagName === "INPUT") return;
            if (/^[0-9*#]$/.test(e.key)) press(e.key);
            else if (e.key === "Backspace") backspace();
            else if (e.key === "Enter") placeCall();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [state, activeCall, incoming, dest, callerNumber]); // eslint-disable-line

    const remoteNum = incoming?.options?.remoteCallerNumber || (outgoing ? dest : (activeCall?.options?.destinationNumber)) || dest;

    return (
        <div
            data-testid="softphone"
            className="relative flex flex-col h-[640px] w-full max-w-[400px] mx-auto bg-paper rounded-[2.25rem] shadow-2xl overflow-hidden border-[6px] border-espresso/15"
        >
            <audio id="flexline-remote-audio" autoPlay playsInline />

            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-espresso/70">FlexLine calling</p>
                <StatusPill state={state} />
            </div>

            {/* Body */}
            <div className="flex-1 min-h-0 relative">
                {state === "idle" && (
                    <div className="h-full flex flex-col items-center justify-center gap-5 px-8 text-center" data-testid="softphone-idle">
                        <div className="w-20 h-20 rounded-full bg-espresso/5 flex items-center justify-center">
                            <Phone className="w-9 h-9 text-espresso" />
                        </div>
                        <p className="text-sm text-zinc-600">Connect your line to make and receive calls right here in the app. You'll be asked to allow microphone access.</p>
                        <motion.button
                            whileTap={{ scale: 0.95 }}
                            onClick={initCall}
                            data-testid="softphone-enable-btn"
                            className="bg-brand-green text-white rounded-full h-12 px-8 text-sm font-medium inline-flex items-center gap-2"
                        >
                            <Phone className="w-4 h-4" /> Enable calling
                        </motion.button>
                    </div>
                )}

                {state === "loading" && (
                    <div className="h-full flex flex-col items-center justify-center gap-3 text-sm text-zinc-500" data-testid="softphone-loading">
                        <Loader2 className="w-7 h-7 animate-spin text-espresso" /> Setting up your line…
                    </div>
                )}

                {(state === "unavailable" || state === "error") && (
                    <div className="h-full flex flex-col items-center justify-center gap-4 px-8 text-center" data-testid="softphone-unavailable">
                        <div className="flex items-start gap-3 text-sm text-zinc-600 bg-cream border border-[#DDD6FE] rounded-2xl p-4 text-left">
                            <Info className="w-4 h-4 mt-0.5 text-espresso shrink-0" />
                            <span>{detail}</span>
                        </div>
                        {state === "error" && (
                            <motion.button whileTap={{ scale: 0.95 }} onClick={initCall} data-testid="softphone-retry-btn"
                                className="rounded-full h-11 px-6 border border-espresso/30 text-espresso text-sm font-medium">Try again</motion.button>
                        )}
                    </div>
                )}

                {state === "ready" && (
                    <AnimatePresence mode="wait">
                        {tab === "keypad" ? (
                            <motion.div key="keypad" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                transition={{ duration: 0.18 }} className="h-full flex flex-col px-6 pb-4" data-testid="softphone-keypad-tab">
                                <div className="flex-1 flex flex-col justify-center">
                                    <div className="text-center mb-5 min-h-[3rem] flex items-center justify-center">
                                        <span className={`font-mono tracking-tight text-espresso ${dest.length > 12 ? "text-2xl" : "text-4xl"}`} data-testid="softphone-dest-display">
                                            {dest || <span className="text-zinc-300">Enter a number</span>}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-3 gap-3 max-w-[300px] mx-auto w-full" data-testid="softphone-dialpad">
                                        {KEYS.map((k) => (
                                            <motion.button key={k.d} whileTap={{ scale: 0.9 }} onClick={() => press(k.d)}
                                                data-testid={`dial-key-${k.d}`} aria-label={`Dial ${k.d}`}
                                                className="aspect-square rounded-full bg-cream hover:bg-[#EEE7F8] active:bg-[#E6DCF7] transition-colors flex flex-col items-center justify-center leading-none">
                                                <span className="text-3xl font-light text-espresso">{k.d}</span>
                                                {k.s && <span className="text-[9px] tracking-[0.2em] text-zinc-400 mt-0.5">{k.s}</span>}
                                            </motion.button>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex items-center justify-center gap-6 pt-4">
                                    <motion.button whileTap={{ scale: 0.9 }} onClick={() => dest.trim() && addFavorite(toE164(dest))} disabled={!dest.trim()}
                                        data-testid="softphone-add-favorite-btn" aria-label="Add to favorites"
                                        className="w-12 h-12 rounded-full flex items-center justify-center text-amber-400 disabled:opacity-20">
                                        <Star className="w-6 h-6" />
                                    </motion.button>
                                    <motion.button whileTap={{ scale: 0.9 }} onClick={() => placeCall()} disabled={!dest.trim()}
                                        data-testid="softphone-call-btn" aria-label="Call"
                                        className="w-16 h-16 rounded-full bg-brand-green text-white flex items-center justify-center shadow-lg disabled:opacity-40 disabled:cursor-not-allowed">
                                        <Phone className="w-7 h-7" />
                                    </motion.button>
                                    <motion.button whileTap={{ scale: 0.9 }} onClick={backspace} disabled={!dest}
                                        data-testid="softphone-backspace-btn" aria-label="Delete"
                                        className="w-12 h-12 rounded-full flex items-center justify-center text-espresso/60 disabled:opacity-20">
                                        <Delete className="w-6 h-6" />
                                    </motion.button>
                                </div>
                            </motion.div>
                        ) : tab === "recents" ? (
                            <motion.div key="recents" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                transition={{ duration: 0.18 }} className="h-full overflow-y-auto px-4 py-2" data-testid="softphone-recents-tab">
                                {recents.length === 0 ? (
                                    <div className="h-full flex flex-col items-center justify-center gap-3 text-center px-8">
                                        <Clock className="w-8 h-8 text-zinc-300" />
                                        <p className="text-sm text-zinc-400">No recent calls yet. Calls you make and receive will show up here.</p>
                                    </div>
                                ) : recents.map((r, i) => (
                                    <button key={i} onClick={() => placeCall(r.number)}
                                        data-testid={`recent-call-${i}`}
                                        className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-cream transition-colors text-left">
                                        <div className="w-10 h-10 rounded-full bg-espresso/5 flex items-center justify-center shrink-0">
                                            <User className="w-5 h-5 text-espresso/50" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-mono text-sm text-espresso truncate">{r.number}</p>
                                            <p className={`text-xs flex items-center gap-1 ${r.direction === "missed" ? "text-[#D97706]" : "text-zinc-400"}`}>
                                                {r.direction === "incoming" && <PhoneIncoming className="w-3 h-3" />}
                                                {r.direction === "outgoing" && <PhoneOutgoing className="w-3 h-3" />}
                                                {r.direction === "missed" && <PhoneMissed className="w-3 h-3" />}
                                                {r.direction} · {fmtTime(r.at)}
                                            </p>
                                        </div>
                                        <Star onClick={(e) => { e.stopPropagation(); addFavorite(r.number); }}
                                            data-testid={`recent-fav-${i}`}
                                            className="w-4 h-4 text-zinc-300 hover:text-amber-400" />
                                        <Phone className="w-4 h-4 text-brand-green" />
                                    </button>
                                ))}
                            </motion.div>
                        ) : (
                            <motion.div key="contacts" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                transition={{ duration: 0.18 }} className="h-full overflow-y-auto px-4 py-2" data-testid="softphone-contacts-tab">
                                {favorites.length === 0 ? (
                                    <div className="h-full flex flex-col items-center justify-center gap-3 text-center px-8">
                                        <Star className="w-8 h-8 text-zinc-300" />
                                        <p className="text-sm text-zinc-400">No favorites yet. Tap the ⭐ on the keypad or a recent call to save a contact for one-tap dialing.</p>
                                    </div>
                                ) : favorites.map((f, i) => (
                                    <div key={i} data-testid={`favorite-${i}`}
                                        className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-cream transition-colors text-left">
                                        <button onClick={() => placeCall(f.number)} data-testid={`favorite-call-${i}`}
                                            className="flex items-center gap-3 flex-1 min-w-0 text-left">
                                            <div className="w-10 h-10 rounded-full bg-amber-400/15 flex items-center justify-center shrink-0">
                                                <User className="w-5 h-5 text-amber-500/70" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm text-espresso truncate font-medium">{f.name || f.number}</p>
                                                {f.name ? <p className="font-mono text-xs text-zinc-400 truncate">{f.number}</p> : null}
                                            </div>
                                            <Phone className="w-4 h-4 text-brand-green" />
                                        </button>
                                        <button onClick={() => removeFavorite(f.number)} data-testid={`favorite-remove-${i}`}
                                            className="p-1 text-zinc-300 hover:text-[#D97706]"><X className="w-4 h-4" /></button>
                                    </div>
                                ))}
                            </motion.div>
                        )}
                    </AnimatePresence>
                )}

                {/* ===== Full-screen call overlays ===== */}
                <AnimatePresence>
                    {incoming && (
                        <CallOverlay key="incoming" testid="softphone-incoming">
                            <div className="flex flex-col items-center gap-4">
                                <motion.div animate={{ scale: [1, 1.08, 1] }} transition={{ repeat: Infinity, duration: 1.5 }}
                                    className="w-28 h-28 rounded-full bg-white/10 flex items-center justify-center">
                                    <User className="w-14 h-14 text-white/80" />
                                </motion.div>
                                <p className="font-mono text-2xl tracking-tight mt-2" data-testid="incoming-caller">{remoteNum || "Unknown"}</p>
                                <p className="text-sm text-white/60 uppercase tracking-[0.2em]">Incoming call</p>
                            </div>
                            <div className="flex items-center justify-between w-full max-w-[260px]">
                                <div className="flex flex-col items-center gap-2">
                                    <motion.button whileTap={{ scale: 0.9 }} onClick={reject} data-testid="softphone-reject-btn" aria-label="Decline"
                                        className="w-16 h-16 rounded-full bg-emerald-deep text-white flex items-center justify-center shadow-lg">
                                        <PhoneOff className="w-7 h-7" />
                                    </motion.button>
                                    <span className="text-xs text-white/60">Decline</span>
                                </div>
                                <div className="flex flex-col items-center gap-2">
                                    <motion.button whileTap={{ scale: 0.9 }} animate={{ scale: [1, 1.06, 1] }} transition={{ repeat: Infinity, duration: 1.2 }}
                                        onClick={answer} data-testid="softphone-answer-btn" aria-label="Answer"
                                        className="w-16 h-16 rounded-full bg-brand-green text-white flex items-center justify-center shadow-lg">
                                        <Phone className="w-7 h-7" />
                                    </motion.button>
                                    <span className="text-xs text-white/60">Accept</span>
                                </div>
                            </div>
                        </CallOverlay>
                    )}

                    {(activeCall || outgoing) && !incoming && (
                        <CallOverlay key="active" testid={callStatus === "active" ? "softphone-active" : "softphone-outgoing"}>
                            <div className="flex flex-col items-center gap-4">
                                <div className="w-28 h-28 rounded-full bg-white/10 flex items-center justify-center">
                                    <User className="w-14 h-14 text-white/80" />
                                </div>
                                <p className="font-mono text-2xl tracking-tight mt-2">{remoteNum}</p>
                                <p className="text-sm text-white/60 uppercase tracking-[0.2em]" data-testid="call-state-label">
                                    {callStatus === "active" ? (
                                        <span className="font-mono tracking-widest text-white/80" data-testid="call-timer">{fmtDuration(seconds)}</span>
                                    ) : callStatus === "held" ? "On hold" : outgoing ? "Calling…" : "Connecting…"}
                                </p>
                            </div>

                            {/* In-call controls */}
                            <div className="w-full max-w-[300px] flex flex-col items-center gap-6">
                                {showInCallPad ? (
                                    <div className="grid grid-cols-3 gap-3 w-full" data-testid="softphone-dtmf-pad">
                                        {KEYS.map((k) => (
                                            <motion.button key={k.d} whileTap={{ scale: 0.9 }} onClick={() => sendDtmf(k.d)} data-testid={`dtmf-key-${k.d}`}
                                                className="aspect-square rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
                                                <span className="text-2xl font-light">{k.d}</span>
                                            </motion.button>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-3 gap-5">
                                        <CallToggle active={muted} onClick={toggleMute} testid="softphone-mute-btn" label={muted ? "Unmute" : "Mute"}
                                            icon={muted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />} />
                                        <CallToggle active={speaker} onClick={toggleSpeaker} testid="softphone-speaker-btn" label="Speaker"
                                            icon={speaker ? <Volume2 className="w-6 h-6" /> : <VolumeX className="w-6 h-6" />} />
                                        <CallToggle active={false} onClick={() => setShowInCallPad(true)} testid="softphone-keypad-btn" label="Keypad"
                                            icon={<Grid3x3 className="w-6 h-6" />} />
                                    </div>
                                )}
                                {showInCallPad && (
                                    <button onClick={() => setShowInCallPad(false)} data-testid="softphone-hide-pad-btn"
                                        className="text-xs text-white/60 uppercase tracking-widest inline-flex items-center gap-1">
                                        <X className="w-3 h-3" /> Hide keypad
                                    </button>
                                )}
                                <motion.button whileTap={{ scale: 0.9 }} onClick={hangup} data-testid="softphone-hangup-btn" aria-label="End call"
                                    className="w-16 h-16 rounded-full bg-emerald-deep text-white flex items-center justify-center shadow-lg">
                                    <PhoneOff className="w-7 h-7" />
                                </motion.button>
                            </div>
                        </CallOverlay>
                    )}
                </AnimatePresence>
            </div>

            {/* Bottom tab bar */}
            {state === "ready" && !activeCall && !incoming && (
                <div className="flex items-center border-t border-[#DDD6FE] bg-paper">
                    <TabBtn active={tab === "keypad"} onClick={() => setTab("keypad")} testid="softphone-tab-keypad" icon={<Grid3x3 className="w-5 h-5" />} label="Keypad" />
                    <TabBtn active={tab === "recents"} onClick={() => setTab("recents")} testid="softphone-tab-recents" icon={<Clock className="w-5 h-5" />} label="Recent" />
                    <TabBtn active={tab === "contacts"} onClick={() => { setTab("contacts"); loadFavorites(); }} testid="softphone-tab-contacts" icon={<Star className="w-5 h-5" />} label="Favorites" />
                </div>
            )}
            {callerNumber && state === "ready" && !activeCall && !incoming && (
                <p className="text-center font-mono text-[10px] text-zinc-400 pb-2">Your number: {callerNumber}</p>
            )}
        </div>
    );
};

const CallOverlay = ({ children, testid }) => (
    <motion.div
        initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
        transition={{ type: "spring", stiffness: 320, damping: 34 }}
        className="absolute inset-0 z-50 bg-espresso text-white flex flex-col items-center justify-between py-14 px-8"
        data-testid={testid}
    >
        {children}
    </motion.div>
);

const CallToggle = ({ active, onClick, icon, label, testid }) => (
    <div className="flex flex-col items-center gap-2">
        <motion.button whileTap={{ scale: 0.9 }} onClick={onClick} data-testid={testid} aria-label={label}
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${active ? "bg-white text-espresso" : "bg-white/10 text-white hover:bg-white/20"}`}>
            {icon}
        </motion.button>
        <span className="text-[11px] text-white/60">{label}</span>
    </div>
);

const TabBtn = ({ active, onClick, icon, label, testid }) => (
    <button onClick={onClick} data-testid={testid}
        className={`flex-1 flex flex-col items-center gap-1 py-3 transition-colors ${active ? "text-espresso" : "text-zinc-400"}`}>
        {icon}
        <span className="text-[10px] uppercase tracking-widest">{label}</span>
    </button>
);

const StatusPill = ({ state }) => {
    const map = {
        idle: { label: "Standby", dot: "bg-zinc-400", cls: "text-zinc-500" },
        ready: { label: "Live", dot: "bg-brand-green", cls: "text-brand-green" },
        loading: { label: "Connecting", dot: "bg-amber-400", cls: "text-zinc-500" },
        unavailable: { label: "Standby", dot: "bg-zinc-400", cls: "text-zinc-500" },
        error: { label: "Offline", dot: "bg-[#D97706]", cls: "text-[#D97706]" },
    };
    const s = map[state] || map.loading;
    return (
        <span className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest ${s.cls}`}>
            <span className={`w-2 h-2 rounded-full ${s.dot} ${state === "ready" ? "animate-pulse" : ""}`} /> {s.label}
        </span>
    );
};
